import { normalizeSettings } from "../platform/preferences";
import { gameStorage } from "../platform/storage";
import type { GameState } from "./types";

const SAVE_KEY = "cinderwake.run.v1";
const BACKUP_KEY = "cinderwake.run.backup.v1";
const HISTORY_KEY = "cinderwake.history.v1";
const META_KEY = "cinderwake.run.meta.v1";

export interface SaveMetadata { savedAt: string; recovered: boolean; source: "primary" | "backup" }
export interface RunHistoryEntry {
  seed: string;
  classId: string;
  result: "victory" | "defeat" | "abandoned";
  act: number;
  floor: number;
  startedAt: string;
  finishedAt: string;
  stats: GameState["stats"];
}

function parseRun(raw: string | null): GameState | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as GameState;
    if (parsed.version !== 1 || !parsed.seed || !parsed.classId || !parsed.deck || !parsed.map) return null;
    return { ...parsed, settings: normalizeSettings(parsed.settings), tutorialComplete: Boolean(parsed.tutorialComplete) };
  } catch { return null; }
}

export function saveRun(state: GameState): void {
  try {
    const existing = gameStorage.getItem(SAVE_KEY);
    if (parseRun(existing)) gameStorage.setItem(BACKUP_KEY, existing!);
    gameStorage.setItem(SAVE_KEY, JSON.stringify({ ...state, settings: normalizeSettings(state.settings) }));
    gameStorage.setItem(META_KEY, JSON.stringify({ savedAt: new Date().toISOString(), recovered: false, source: "primary" } satisfies SaveMetadata));
  } catch { /* private browsing or storage quota */ }
}

export function loadRun(): GameState | null {
  try {
    const primary = parseRun(gameStorage.getItem(SAVE_KEY));
    if (primary) return primary;
    const backup = parseRun(gameStorage.getItem(BACKUP_KEY));
    if (!backup) return null;
    gameStorage.setItem(SAVE_KEY, JSON.stringify(backup));
    gameStorage.setItem(META_KEY, JSON.stringify({ savedAt: new Date().toISOString(), recovered: true, source: "backup" } satisfies SaveMetadata));
    return { ...backup, message: "Recovered the expedition from the last valid checkpoint." };
  } catch { return null; }
}

export function exportRun(state: GameState): string { return JSON.stringify({ ...state, settings: normalizeSettings(state.settings) }, null, 2); }
export function importRun(serialized: string): GameState {
  const parsed = parseRun(serialized);
  if (!parsed) throw new Error("This is not a compatible Cinderwake run.");
  return parsed;
}

export function clearRun(): void {
  try { gameStorage.removeItem(SAVE_KEY); gameStorage.removeItem(BACKUP_KEY); gameStorage.removeItem(META_KEY); } catch { /* ignored */ }
}

export function recordRun(state: GameState, result: RunHistoryEntry["result"]): void {
  try {
    const history = loadHistory();
    history.unshift({ seed: state.seed, classId: state.classId, result, act: state.act, floor: state.floor, startedAt: state.stats.startedAt, finishedAt: new Date().toISOString(), stats: state.stats });
    gameStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(0, 40)));
  } catch { /* ignored */ }
}

export function loadHistory(): RunHistoryEntry[] {
  try { return JSON.parse(gameStorage.getItem(HISTORY_KEY) ?? "[]") as RunHistoryEntry[]; } catch { return []; }
}
